// ReportesReservasCatalog.jsx - Componente de reporte de reservas con exportación a Excel
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsCarBooking';
import reporteReservaModel from '../../../Utils/reportesReservasModel';
import { normalizeValue } from '../../../../../utils/Texts';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';
import { useResourcePermissions } from '../../../../../utils/useResourcePermissions';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Reporte de Reservas";

export default function ReportesReservasCatalog() {
    const { puedeExportar } = useResourcePermissions('ReportesReservas');
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);
    const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

    const triggerAlert = (message, variant = 'error', title = 'Error') => {
        setAlertConfig({ open: true, message, variant, title });
    };

    // Función para parsear direcciones JSON
    const parseAddress = (jsonString) => {
        if (!jsonString) return 'N/A';
        try {
            const parsed = JSON.parse(jsonString);
            return parsed.address || 'N/A';
        } catch {
            return jsonString || 'N/A';
        }
    };

    // Función para obtener el nombre del estado
    const getEstadoNombre = (idEstado) => {
        switch (idEstado) {
            case 1: return 'Pendiente de autorización';
            case 2: return 'Confirmada';
            case 3: return 'Autorizada';
            case 4: return 'Rechazada';
            case 5: return 'Cancelada';
            case 6: return 'Completada';
            default: return 'Desconocido';
        }
    };

    // Función para formatear fecha sin hora
    const formatDateOnly = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? dateString : date.toLocaleDateString('es-MX');
    };

    // Cargar datos del reporte
    const loadData = async () => {
        setLoading(true);
        try {
            // 1. Cargar todas las reservas
            const resReservas = await axios.get(ENDPOINTS.reserva);

            if (!resReservas.data || resReservas.data.length === 0) {
                setRows([]);
                setLoading(false);
                return;
            }

            const reservas = (resReservas.data || []).map(Models.reserva);

            // 2. Cargar catálogos auxiliares en paralelo
            const [
                resChoferes,
                resEmpleados,
                resPersonas,
                resTiposAprobacion,
                resUnidadesOrgEmpleado,
                resUnidadesOrg,
                resEmpresasUO,
                resEmpresas,
                resSolicitantes,
                resVehiculos
            ] = await Promise.all([
                axios.get(ENDPOINTS.chofer),
                axios.get(ENDPOINTS.empleados),
                axios.get(ENDPOINTS.personas),
                axios.get(ENDPOINTS.tipoAprobacion),
                axios.get(ENDPOINTS.unidadesOrgEmpleado),
                axios.get(ENDPOINTS.unidadesOrg),
                axios.get(ENDPOINTS.empresasUnidadesOrg),
                axios.get(ENDPOINTS.empresas),
                axios.get(ENDPOINTS.solicitantes),
                axios.get(ENDPOINTS.vehiculo)
            ]);

            const choferes = (resChoferes.data || []).map(Models.chofer);
            const empleados = (resEmpleados.data || []).map(Models.empleados);
            const personas = (resPersonas.data || []).map(Models.persona);
            const tiposAprobacion = (resTiposAprobacion.data || []).map(Models.tipoAprobacion);
            const unidadesOrgEmpleado = (resUnidadesOrgEmpleado.data || []).map(Models.unidadOrgEmpleado);
            const unidadesOrg = (resUnidadesOrg.data || []).map(Models.unidadesOrg);
            const empresasUO = (resEmpresasUO.data || []).map(Models.empresasUnidadesOrg);
            const empresas = (resEmpresas.data || []).map(Models.empresas);
            const solicitantes = (resSolicitantes.data || []).map(Models.solicitantes);
            const vehiculos = (resVehiculos.data || []).map(Models.vehiculo);

            // Construir el reporte uniendo todos los datos
            const reporteData = reservas.map(reserva => {
                // Obtener chofer
                const chofer = choferes.find(c => c.idChofer === reserva.idChofer);
                const empleadoChofer = chofer ? empleados.find(e => e.idEmpleado === chofer.idEmpleado) : null;
                const personaChofer = empleadoChofer ? personas.find(p => p.idPersona === empleadoChofer.idPersona) : null;
                const nombreChofer = personaChofer
                    ? `${personaChofer.nombre} ${personaChofer.apellidoPaterno || ''} ${personaChofer.apellidoMaterno || ''}`.trim()
                    : 'N/A';

                // Obtener empresa del chofer
                const uoEmpleadoChofer = empleadoChofer 
                    ? unidadesOrgEmpleado.find(uo => uo.idEmpleado === empleadoChofer.idEmpleado)
                    : null;
                const unidadOrgChofer = uoEmpleadoChofer
                    ? unidadesOrg.find(uo => uo.idUnidadOrganizacional === uoEmpleadoChofer.idUnidadOrganizacional)
                    : null;
                const empresaUOChofer = unidadOrgChofer
                    ? empresasUO.find(euo => euo.idUnidadOrganizacional === unidadOrgChofer.idUnidadOrganizacional)
                    : null;
                const empresaChofer = empresaUOChofer
                    ? empresas.find(e => e.idEmpresa === empresaUOChofer.idEmpresa)
                    : null;

                // Obtener solicitante (Cadena: Reserva -> Solicitante -> Empleado -> Persona)
                const solicitante = solicitantes.find(s => s.idSolicitante === reserva.idSolicitante);
                const empleadoSolicitante = solicitante
                    ? empleados.find(e => e.idEmpleado === solicitante.idEmpleado)
                    : null;
                const personaSolicitante = empleadoSolicitante
                    ? personas.find(p => p.idPersona === empleadoSolicitante.idPersona)
                    : null;
                const nombreSolicitante = personaSolicitante
                    ? `${personaSolicitante.nombre} ${personaSolicitante.apellidoPaterno || ''} ${personaSolicitante.apellidoMaterno || ''}`.trim()
                    : 'N/A';

                // Obtener unidad organizacional del solicitante
                // 1. Buscar relación Empleado -> UO
                const uoEmpleado = empleadoSolicitante
                    ? unidadesOrgEmpleado.find(uo => uo.idEmpleado === empleadoSolicitante.idEmpleado)
                    : null;

                // 2. Buscar objeto UO usando idUnidadOrganizacional y formatear nombre
                const unidadOrg = uoEmpleado
                    ? unidadesOrg.find(uo => uo.idUnidadOrganizacional === uoEmpleado.idUnidadOrganizacional)
                    : null;

                const nombreUnidadOrganizacional = unidadOrg
                    ? `${unidadOrg.nombre} - (${unidadOrg.clave || 'N/A'})`
                    : 'N/A';

                // Obtener empresa de la unidad organizacional
                // 3. Buscar relación UO -> Empresa usando idUnidadOrganizacional
                const empresaUO = unidadOrg
                    ? empresasUO.find(euo => euo.idUnidadOrganizacional === unidadOrg.idUnidadOrganizacional)
                    : null;

                // 4. Buscar objeto Empresa usando idEmpresa
                const empresa = empresaUO
                    ? empresas.find(e => e.idEmpresa === empresaUO.idEmpresa)
                    : null;

                // Obtener tipo de aprobación
                const tipoAprobacion = reserva.idTipoAprobacion
                    ? tiposAprobacion.find(ta => ta.idTipoAprobacion === reserva.idTipoAprobacion)
                    : null;

                // Obtener vehículo
                const vehiculo = reserva.idVehiculo
                    ? vehiculos.find(v => String(v.idVehiculo) === String(reserva.idVehiculo))
                    : null;
                const nombreVehiculo = vehiculo
                    ? `${vehiculo.marca} ${vehiculo.modelo} - (${vehiculo.placa})`
                    : 'No registrado';

                // Construir objeto del reporte
                const reporte = {
                    idReserva: reserva.idReserva,
                    nombreChofer,
                    idChofer: reserva.idChofer,
                    fechaInicio: formatDateOnly(reserva.fechaInicio),
                    horaInicio: reserva.horaInicio,
                    fechaFin: formatDateOnly(reserva.fechaFin),
                    horaFin: reserva.horaFin,
                    origen: parseAddress(reserva.origenTexto),
                    destino: parseAddress(reserva.destinoTexto),
                    nombreSolicitante,
                    idSolicitante: reserva.idSolicitante,
                    motivo: reserva.motivo,
                    nombreEmpresa: empresa?.nombre || 'N/A',
                    idEmpresa: empresa?.idEmpresa || 0,
                    nombreUnidadOrganizacional,
                    idUnidadOrganizacional: unidadOrg?.idUnidadOrganizacional || 0,
                    nombreTipoAprobacion: tipoAprobacion?.nombre || 'N/A',
                    idTipoAprobacion: reserva.idTipoAprobacion,
                    nombreEstado: getEstadoNombre(reserva.idEstadoReserva),
                    idEstadoReserva: reserva.idEstadoReserva,
                    observaciones: reserva.observaciones,
                    fechaCreacion: reserva.fechaCreacion,
                    nombreVehiculo, // Nuevo campo
                    idEmpresaChofer: empresaChofer?.idEmpresa || 0
                };
                return reporteReservaModel(reporte);
            });
            console.log('reporteData', reporteData);
            // Filtrado Multi-tenant (Hardening)
            const isSuperAdmin = isSessionSuperAdmin();
            const sessionEmpresaId = getSessionEmpresaId();

            let finalData = reporteData;
            if (!isSuperAdmin) {
                finalData = reporteData.filter(r => 
                    r.idEmpresa == sessionEmpresaId || 
                    r.idEmpresaChofer == sessionEmpresaId
                );
            }

            setRows(finalData);
        } catch (error) {
            console.error('Error al cargar el reporte:', error);
            triggerAlert('Error al cargar el reporte de reservas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Filtrado
    const filtered = useMemo(() => {
        console.log('2', rows);
        if (!query) return rows;
        const q = normalizeValue(query);
        return rows.filter(r => {
            const vals = [
                r.nombreChofer,
                r.nombreSolicitante,
                r.motivo,
                r.nombreEmpresa,
                r.nombreUnidadOrganizacional,
                r.nombreTipoAprobacion,
                r.nombreEstado,
                r.origen,
                r.destino,
                r.nombreVehiculo
            ].map(v => normalizeValue(v || ''));
            return vals.some(v => v.includes(q));
        });
    }, [rows, query]);

    // Paginación
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    // Exportar a Excel (CSV)
    const downloadExcel = () => {
        if (!rows || rows.length === 0) {
            triggerAlert("No hay datos para exportar.", "warning", "Atención");
            return;
        }

        // Encabezados (Nuevo orden)
        const headers = [
            "Fecha Inicio", "Hora Inicio", "Fecha Fin", "Hora Fin",
            "Estado", "Solicitante", "Chofer", "Vehículo",
            "Origen", "Destino", "Motivo", "Empresa",
            "Unidad Org.", "Tipo Aprob.", "Observaciones", "Fecha Creación"
        ];

        // Mapeo de datos
        const csvRows = [
            headers.join(","), // Fila de encabezados
            ...rows.map(row => {
                return [
                    `"${row.fechaInicio || ''}"`,
                    `"${row.horaInicio || ''}"`,
                    `"${row.fechaFin || ''}"`,
                    `"${row.horaFin || ''}"`,
                    `"${row.nombreEstado || ''}"`,
                    `"${row.nombreSolicitante || ''}"`,
                    `"${row.nombreChofer || ''}"`,
                    `"${(row.nombreVehiculo || '').replace(/"/g, '""')}"`,
                    `"${(row.origen || '').replace(/"/g, '""')}"`,
                    `"${(row.destino || '').replace(/"/g, '""')}"`,
                    `"${(row.motivo || '').replace(/"/g, '""')}"`,
                    `"${row.nombreEmpresa || ''}"`,
                    `"${row.nombreUnidadOrganizacional || ''}"`,
                    `"${row.nombreTipoAprobacion || ''}"`,
                    `"${(row.observaciones || '').replace(/"/g, '""')}"`,
                    `"${formatDateOnly(row.fechaCreacion)}"`
                ].join(",");
            })
        ];

        const csvString = "\uFEFF" + csvRows.join("\n"); // BOM para UTF-8
        const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Reporte_Reservas_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>

                    <input
                        className="roles-search"
                        placeholder="Buscar por chofer, solicitante, motivo..."
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setPage(1);
                        }}
                    />
                    {puedeExportar && (
                        <button className="roles-btn" onClick={downloadExcel} title="Descargar Excel" style={{ marginRight: '10px' }}>
                            📥 Descargar Excel
                        </button>
                    )}

                </div>

                {loading ? (
                    <div className="catalog-loading">Cargando reporte...</div>
                ) : (
                    <div className="roles-table-wrap">
                        <div className="roles-table-scroller">
                            <table className="roles-table">
                                <thead>
                                    <tr>
                                        <th>Fecha Inicio</th>
                                        <th>Hora Inicio</th>
                                        <th>Fecha Fin</th>
                                        <th>Hora Fin</th>
                                        <th>Estado</th>
                                        <th>Solicitante</th>
                                        <th>Chofer</th>
                                        <th>Vehículo</th>
                                        <th>Origen</th>
                                        <th>Destino</th>
                                        <th>Motivo</th>
                                        <th>Empresa</th>
                                        <th>Unidad Org.</th>
                                        <th>Tipo Aprob.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.length === 0 ? (
                                        <tr>
                                            <td colSpan="14" style={{ textAlign: 'center' }}>
                                                No se encontraron registros.
                                            </td>
                                        </tr>
                                    ) : (
                                        paginated.map((row, index) => {
                                            console.log('row', row);
                                            return (
                                                <tr key={row.idReserva || index}>
                                                    <td>{row.fechaInicio || 'N/A'}</td>
                                                    <td>{row.horaInicio || 'N/A'}</td>
                                                    <td>{row.fechaFin || 'N/A'}</td>
                                                    <td>{row.horaFin || 'N/A'}</td>
                                                    <td>{row.nombreEstado || 'N/A'}</td>
                                                    <td>{row.nombreSolicitante || 'N/A'}</td>
                                                    <td>{row.nombreChofer || 'N/A'}</td>
                                                    <td>{row.nombreVehiculo || 'N/A'}</td>
                                                    <td title={row.origen}>
                                                        {row.origen ? (row.origen.length > 20 ? row.origen.substring(0, 20) + '...' : row.origen) : 'N/A'}
                                                    </td>
                                                    <td title={row.destino}>
                                                        {row.destino ? (row.destino.length > 20 ? row.destino.substring(0, 20) + '...' : row.destino) : 'N/A'}
                                                    </td>
                                                    <td title={row.motivo}>
                                                        {row.motivo ? (row.motivo.length > 20 ? row.motivo.substring(0, 20) + '...' : row.motivo) : 'N/A'}
                                                    </td>
                                                    <td>{row.nombreEmpresa || 'N/A'}</td>
                                                    <td>{row.nombreUnidadOrganizacional || 'N/A'}</td>
                                                    <td>{row.nombreTipoAprobacion || 'N/A'}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        {filtered.length > 0
                            ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                            : <span>0 resultados</span>}
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn
                            label="‹"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                        />
                        {pageNumbers.map(n => (
                            <PageBtn
                                key={n}
                                label={n}
                                onClick={() => setPage(n)}
                                active={n === page}
                            />
                        ))}
                        <PageBtn
                            label="›"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                        />
                    </div>
                </div>
            </div>

            <AlertDialog
                open={alertConfig.open}
                title={alertConfig.title}
                message={alertConfig.message}
                variant={alertConfig.variant}
                onClose={() => setAlertConfig({ ...alertConfig, open: false })}
            />
        </div>
    );
}
